import * as React from 'react'
import { TmsDataModel } from '@/app/models/tmsDataModel'
import useBusinessTripService, { TmsSaveItem } from '@/modules/drivers/services/BusinessTripService'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { toast } from '@/lib/toasts/api'

export type TmsSourceMode = 'driver' | 'vehicle'

interface Options {
  companyId: string
  driverId: string | number
  initialDateFrom: string
  initialDateTo: string
  isInitiallyChecked?: (item: TmsDataModel) => boolean
}

// Odpowiednik WPF TmsDataViewModel: lista ładunków kierowcy albo pojazdu,
// edycje i usunięcia trzymane lokalnie i zapisywane do bazy przy „Zapisz”.
export const useTmsLoadsEditor = ({
  companyId,
  driverId,
  initialDateFrom,
  initialDateTo,
  isInitiallyChecked,
}: Options) => {
  const { t } = useLocalize()
  const { getTmsData, saveTmsData } = useBusinessTripService()

  const [items, setItems] = React.useState<TmsDataModel[]>([])
  const [isLoading, setIsLoading] = React.useState(false)
  const [dateFrom, setDateFrom] = React.useState(initialDateFrom)
  const [dateTo, setDateTo] = React.useState(initialDateTo)
  const [mode, setMode] = React.useState<TmsSourceMode>('driver')
  const [vehicleId, setVehicleId] = React.useState<string | null>(null)
  // id wiersza -> edytowane pola; kraj wysyłamy tylko, gdy użytkownik go zmienił.
  const [edited, setEdited] = React.useState<Map<number, Set<keyof TmsDataModel>>>(new Map())
  const [removedIds, setRemovedIds] = React.useState<number[]>([])
  const [pendingRemoval, setPendingRemoval] = React.useState<number[] | null>(null)

  // Bez wybranego pojazdu WPF ładuje dane kierowcy także w trybie „Pojazd”.
  const sourceVehicleId = mode === 'vehicle' ? vehicleId : null

  React.useEffect(() => {
    let isStale = false
    // Jak LoadTmsData w WPF: przeładowanie porzuca niezapisane zmiany.
    const apply = (next: TmsDataModel[]) => {
      if (isStale) return
      setItems(next)
      setEdited(new Map())
      setRemovedIds([])
    }

    const load = async () => {
      if (!driverId || !dateFrom || !dateTo) {
        apply([])
        return
      }

      try {
        setIsLoading(true)
        const data = await getTmsData({
          companyId,
          driverId,
          startTime: dateFrom,
          endTime: dateTo,
          vehicleId: sourceVehicleId,
        })
        apply(
          (data || []).map((item: TmsDataModel) => ({
            ...item,
            checked: isInitiallyChecked ? isInitiallyChecked(item) : false,
          })),
        )
      } catch (err: any) {
        if (isStale) return
        console.error('Error fetching TMS data:', err)
        if (err?.response || err?.message) {
          toast.error({
            text:
              err?.response?.data?.message ||
              err?.response?.data?.error ||
              err?.message ||
              t('errorCodes.retrieving-tms-data-failed'),
          })
        }
        apply([])
      } finally {
        if (!isStale) setIsLoading(false)
      }
    }

    load()
    return () => {
      isStale = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, driverId, dateFrom, dateTo, sourceVehicleId])

  const toggleAll = (checked: boolean) =>
    setItems((prev) => prev.map((item) => ({ ...item, checked })))

  const toggleItem = (id: number | undefined, checked: boolean) => {
    if (!id) return
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, checked } : item)),
    )
  }

  const updateItem = (id: number | undefined, updates: Partial<TmsDataModel>) => {
    if (!id) return
    setItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...updates } : item)),
    )
    setEdited((prev) => {
      const next = new Map(prev)
      const fields = new Set(next.get(id))
      Object.keys(updates).forEach((key) => fields.add(key as keyof TmsDataModel))
      next.set(id, fields)
      return next
    })
  }

  const confirmRemoval = () => {
    const ids = pendingRemoval ?? []
    setItems((prev) => prev.filter((item) => !item.id || !ids.includes(item.id)))
    setRemovedIds((prev) => [...prev, ...ids])
    setPendingRemoval(null)
  }

  // WPF oddaje zaznaczone posortowane po dacie załadunku, potem rozładunku.
  const selectedItems = items
    .filter((item) => item.checked)
    .sort(
      (a, b) =>
        a.loadingDate.valueOf() - b.loadingDate.valueOf() ||
        a.unloadingDate.valueOf() - b.unloadingDate.valueOf(),
    )

  const hasChanges = edited.size > 0 || removedIds.length > 0

  // WPF OnSave: zaznaczone nie mogą mieć powtórzonego numeru dokumentu.
  const validateSelection = (): boolean => {
    const counts = selectedItems.reduce<Map<string, number>>((acc, item) => {
      const key = item.docNumber ?? ''
      acc.set(key, (acc.get(key) ?? 0) + 1)
      return acc
    }, new Map())
    const duplicates = [...counts].filter(([, n]) => n > 1).map(([key]) => key)
    if (duplicates.length > 0) {
      toast.error({
        text: t('tmsDownload.duplicated-doc-numbers').replace('{0}', duplicates.join(', ')),
      })
      return false
    }
    return true
  }

  const persistChanges = async (): Promise<boolean> => {
    if (!hasChanges) return true
    const changedItems = items.filter((item) => item.id && edited.has(item.id))
    if (changedItems.some((item) => !item.loadingDate?.isValid() || !item.unloadingDate?.isValid())) {
      toast.error({ text: t('tmsDownload.dates-required') })
      return false
    }
    const changed: TmsSaveItem[] = changedItems.map((item) => {
      const fields = edited.get(item.id as number)
      return {
        id: item.id as number,
        docNumber: item.docNumber ?? null,
        loadingDate: item.loadingDate,
        unloadingDate: item.unloadingDate,
        loadingCountry: fields?.has('loadingCountry') ? item.loadingCountry?.name : undefined,
        unloadingCountry: fields?.has('unloadingCountry') ? item.unloadingCountry?.name : undefined,
      }
    })

    try {
      await saveTmsData({ companyId, driverId, changed, removedIds })
      setEdited(new Map())
      setRemovedIds([])
      return true
    } catch (err) {
      console.error('Error saving TMS data:', err)
      toast.error({ text: t('tmsDownload.save-failed') })
      return false
    }
  }

  return {
    items,
    isLoading,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    mode,
    setMode,
    vehicleId,
    setVehicleId,
    toggleAll,
    toggleItem,
    updateItem,
    pendingRemoval,
    requestRemoval: setPendingRemoval,
    cancelRemoval: () => setPendingRemoval(null),
    confirmRemoval,
    selectedItems,
    hasChanges,
    validateSelection,
    persistChanges,
  }
}

export type TmsLoadsEditorState = ReturnType<typeof useTmsLoadsEditor>
